// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import { StatusCritical } from 'grommet-icons'
import { isEmpty } from 'lodash'

import { Notification, Typography } from '../../../components'
import { PreferencesModal } from '../components/PreferencesModal'
import { Widget } from '../components/Widget'

import { NotificationsLogsPage } from './components/NotificationLogsPage'
import { NotificationLoader, RenderNoDataInfo } from './utils'

const AlertsEvents = () => {
  const { t } = useTranslation(['manage'])
  const [errorMessage, setErrorMessage] = useState(null)
  const [openPreferencesModal, setOpenPreferencesModal] = useState(false)
  const [initialSettings, setInitialSettings] = useState({})
  const [preferenceError, setPreferenceError] = useState(null)
  const navigate = useNavigate()
  const [count, setCount] = useState({
    UNKNOWN: null,
    CRITICAL: null,
    WARNING: null
  })
  const [isLoader, setIsLoader] = useState(true)
  const [showNotificationComponents, setShowNotificationComponents] =
    useState(false)
  const [notificationsExists, setNotificationsExists] = useState(null)
  const [isDayZero, setIsDayZero] = useState(false)

  // when both the response from API and the count of alerts are available, set the loader to false
  useEffect(() => {
    if (notificationsExists !== null && count.CRITICAL !== null) {
      setIsLoader(false)
      if (notificationsExists) {
        setShowNotificationComponents(true)
        setIsDayZero(false)
      } else {
        setIsDayZero(true)
        setShowNotificationComponents(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsExists, count])

  return (
    <>
      <Box direction="column" flex>
        <Typography level="3" testId="t15" type="heading" size="large">
          {t('notification.alerts_tasks.sub_heading_alerts')}
        </Typography>
        {showNotificationComponents ? (
          <Widget
            text1={t('notification.status_critical')}
            text2={t('notification.status_warning')}
            text3={t('notification.status_unknown')}
            cnt1={count.CRITICAL}
            cnt2={count.WARNING}
            cnt3={count.UNKNOWN}
            display="Active alerts"
            t={t}
          />
        ) : null}
        {isDayZero && <RenderNoDataInfo t={t} type="ALERTSTASKS" />}
        {isLoader && <NotificationLoader />}
        <Box
          margin={{ top: 'medium' }}
          width="100%"
          direction="column"
          pad={{ right: 'small' }}
        >
          <NotificationsLogsPage
            testId="notifications-datatable"
            setErrorMessage={setErrorMessage}
            type="ALERTSTASKS"
            setCount={setCount}
            showNotificationComponents={showNotificationComponents}
            setNotificationsExists={setNotificationsExists}
            setIsLoader={setIsLoader}
          />
        </Box>
      </Box>
      {errorMessage && (
        <Notification
          backgroundColor="status-critical"
          onClose={() => setErrorMessage(null)}
          testId="critical-inline-notification"
          text={errorMessage}
          icon={<StatusCritical size="medium" />}
        />
      )}
      {preferenceError && (
        <Notification
          backgroundColor="status-critical"
          onClose={() => setPreferenceError(null)}
          testId="critical-inline-notification"
          text={preferenceError}
          icon={<StatusCritical size="medium" />}
        />
      )}
      {openPreferencesModal && !isEmpty(initialSettings) && (
        <PreferencesModal
          onSetOpen={setOpenPreferencesModal}
          initialSettings={initialSettings}
          setPreferenceError={setPreferenceError}
          setInitialSettings={setInitialSettings}
          navigate={navigate}
        />
      )}
    </>
  )
}

const AlertsTasksPage = () => {
  return <AlertsEvents />
}

export default AlertsTasksPage
